import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button, Progress, Skeleton, Space, Tag, Typography } from 'antd';
import { CheckCircleFilled, ReloadOutlined } from '@ant-design/icons';
import api from '../services/api';
import { TrainingVideo, VideoProgress, VideoSubtitleTrack } from '../types';
import { getApiErrorMessage } from '../utils/apiError';
import './VideoSubtitles.css';

const resolvePlaybackUrl = (url: string) => {
  if (/^https?:\/\//.test(url)) return url;
  const base = String(api.defaults.baseURL || '');
  return /^https?:\/\//.test(base) ? `${new URL(base).origin}${url}` : url;
};
const SESSION_CHANGED = 'Сессия изменилась. Откройте материал заново, чтобы продолжить просмотр.';
const nativeErrorCode = (value: unknown) => typeof value === 'number' && Number.isInteger(value) && value >= 1 && value <= 4 ? value : 0;
const mediaErrorText = (code: number) => {
  const reason = [
    'Причина ошибки не определена.',
    'Загрузка записи была прервана.',
    'Ошибка сети при загрузке записи.',
    'Браузеру не удалось декодировать запись.',
    'Браузер не смог открыть источник или формат записи.',
  ][code];
  return `Не удалось воспроизвести видеофайл. ${reason} Код V${code}`;
};
const boundedMediaState = (value: unknown, maximum: number) => typeof value === 'number' && Number.isFinite(value)
  ? Math.max(0, Math.min(maximum, Math.trunc(value))) : 0;
const codecSupport = (player: HTMLVideoElement, type: string) => {
  try {
    const support = player.canPlayType(type);
    return support === 'probably' ? 2 : support === 'maybe' ? 1 : 0;
  } catch { return 0; }
};
type SubtitleChoice = 'off' | 'ru' | 'en';
interface PlaybackResponse { url: string; subtitles?: VideoSubtitleTrack[] }
const subtitleLabel = (language: string) => language === 'ru' ? 'Русский' : 'English';
const availableSubtitles = (tracks?: VideoSubtitleTrack[]): VideoSubtitleTrack[] => ['ru', 'en'].reduce<VideoSubtitleTrack[]>((result, language) => {
  const track = Array.isArray(tracks) ? tracks.find(item => item.language === language && typeof item.url === 'string' && item.url.trim()) : undefined;
  if (track) result.push(track);
  return result;
}, []);
const mediaIdentity = (url: string) => {
  const parsed = new URL(resolvePlaybackUrl(url), window.location.origin);
  return `${parsed.origin}${parsed.pathname}?v=${parsed.searchParams.get('v') || ''}`;
};

/** Caption changes never reload, pause, seek or replace the owned video node. */
function useVideoSubtitles({ tracks: initialTracks, playbackUrl, mediaGeneration, loading, playerRef, videoId, adminPreview, subtitlesRefresh, sessionIsCurrent }: {
  tracks: VideoSubtitleTrack[]; playbackUrl: string; mediaGeneration: number; loading: boolean;
  playerRef: React.RefObject<HTMLVideoElement>; videoId: number; adminPreview: boolean; subtitlesRefresh: number;
  sessionIsCurrent: () => boolean;
}) {
  const [replacement, setReplacement] = useState<{ url: string; generation: number; tracks: VideoSubtitleTrack[] } | null>(null);
  const [selected, setSelected] = useState<SubtitleChoice>('off');
  const [captionGeneration, setCaptionGeneration] = useState(0);
  const [captionError, setCaptionError] = useState('');
  const [busy, setBusy] = useState(false);
  const alive = useRef(true);
  const desired = useRef<SubtitleChoice>('off');
  const userChoice = useRef<SubtitleChoice | null>(null);
  const applying = useRef(false);
  const modeReady = useRef(false);
  const trackGeneration = useRef(0);
  const errorSource = useRef('');
  const requestNumber = useRef(0);
  const controller = useRef<AbortController | null>(null);
  const lastRefresh = useRef(subtitlesRefresh);
  const pendingRefresh = useRef(false);
  const latest = useRef({ playbackUrl, mediaGeneration }); latest.current = { playbackUrl, mediaGeneration };
  const tracks = useMemo(() => availableSubtitles(replacement?.url === playbackUrl && replacement.generation === mediaGeneration ? replacement.tracks : initialTracks),
    [replacement, playbackUrl, mediaGeneration, initialTracks]);
  const nodes = useCallback(() => Array.from(playerRef.current?.querySelectorAll<HTMLTrackElement>('track[data-subtitle-language]') || []), [playerRef]);
  const apply = useCallback(() => {
    const player = playerRef.current;
    if (!alive.current || !player || !sessionIsCurrent()) return;
    applying.current = true;
    const list = nodes();
    try {
      list.forEach(node => {
        if (!node.track) return;
        const mode = node.srclang === desired.current ? 'showing' : 'disabled';
        if (node.track.mode !== mode) node.track.mode = mode;
      });
      modeReady.current = player.readyState >= HTMLMediaElement.HAVE_METADATA && list.some(node => Boolean(node.track));
    } finally { applying.current = false; }
  }, [nodes, playerRef, sessionIsCurrent]);
  const fail = useCallback((message: string, source: string) => { errorSource.current = source; setCaptionError(message); }, []);

  useEffect(() => {
    alive.current = true;
    return () => { alive.current = false; controller.current?.abort(); };
  }, []);
  useEffect(() => {
    controller.current?.abort(); requestNumber.current += 1; modeReady.current = false;
    setReplacement(null); setBusy(false); setCaptionError(''); errorSource.current = '';
  }, [playbackUrl, mediaGeneration]);
  useEffect(() => {
    // A full retry temporarily clears metadata; keep an explicit choice until its replacement arrives.
    if (loading || !playbackUrl) return;
    let next: SubtitleChoice = userChoice.current === null ? (tracks.some(track => track.language === 'ru') ? 'ru' : 'off') : userChoice.current;
    if (next !== 'off' && !tracks.some(track => track.language === next)) { next = 'off'; userChoice.current = 'off'; }
    desired.current = next; setSelected(next); apply();
  }, [tracks, loading, playbackUrl, apply]);
  useEffect(() => {
    const player = playerRef.current;
    const list = player?.textTracks;
    if (!player || !playbackUrl || loading) return;
    const added = () => apply();
    const changed = () => {
      if (!alive.current || !sessionIsCurrent() || applying.current || !modeReady.current) return;
      const showing = nodes().find(node => node.track?.mode === 'showing');
      const next: SubtitleChoice = showing ? showing.srclang as SubtitleChoice : 'off';
      if (next === desired.current) return;
      desired.current = next; userChoice.current = next; setSelected(next);
      errorSource.current = ''; setCaptionError(''); apply();
    };
    list?.addEventListener?.('addtrack', added); list?.addEventListener?.('change', changed);
    // Apply modes after the fresh video's track nodes are attached, including a full retry.
    const frame = window.requestAnimationFrame(apply);
    return () => { list?.removeEventListener?.('addtrack', added); list?.removeEventListener?.('change', changed); window.cancelAnimationFrame(frame); };
  }, [playbackUrl, mediaGeneration, captionGeneration, loading, apply, nodes, playerRef, sessionIsCurrent]);

  const refresh = useCallback(async () => {
    if (!sessionIsCurrent()) { fail(SESSION_CHANGED, 'session'); return; }
    const player = playerRef.current;
    const sourceUrl = latest.current.playbackUrl;
    const generation = latest.current.mediaGeneration;
    if (!player || !sourceUrl) { pendingRefresh.current = true; return; }
    const request = ++requestNumber.current;
    controller.current?.abort(); const abort = new AbortController(); controller.current = abort;
    errorSource.current = ''; setCaptionError(''); setBusy(true);
    const stillOwned = () => alive.current && !abort.signal.aborted && requestNumber.current === request &&
      playerRef.current === player && latest.current.playbackUrl === sourceUrl && latest.current.mediaGeneration === generation;
    try {
      const prefix = adminPreview ? '/video/admin/videos' : '/video/videos';
      const { data } = await api.get<PlaybackResponse>(`${prefix}/${videoId}/playback-url`, { signal: abort.signal });
      if (!stillOwned()) return;
      if (!sessionIsCurrent()) { fail(SESSION_CHANGED, 'session'); return; }
      if (!data.url || mediaIdentity(data.url) !== mediaIdentity(sourceUrl)) {
        fail('Видеозапись обновлена. Нажмите «Повторить запуск», чтобы открыть новую запись и её субтитры.', 'revision'); return;
      }
      modeReady.current = false; trackGeneration.current += 1;
      setReplacement({ url: sourceUrl, generation, tracks: availableSubtitles(data.subtitles) });
      setCaptionGeneration(trackGeneration.current);
    } catch (err) {
      if (stillOwned()) fail(sessionIsCurrent() ? getApiErrorMessage(err, 'Не удалось обновить субтитры. Повторите загрузку.') : SESSION_CHANGED, sessionIsCurrent() ? 'refresh' : 'session');
    } finally { if (stillOwned()) setBusy(false); }
  }, [adminPreview, fail, playerRef, sessionIsCurrent, videoId]);
  useEffect(() => {
    if (subtitlesRefresh > lastRefresh.current) pendingRefresh.current = true;
    lastRefresh.current = subtitlesRefresh;
    if (pendingRefresh.current && playbackUrl && !loading) { pendingRefresh.current = false; void refresh(); }
  }, [subtitlesRefresh, playbackUrl, mediaGeneration, loading, refresh]);

  const choose = (value: SubtitleChoice) => {
    if (!sessionIsCurrent()) { fail(SESSION_CHANGED, 'session'); return; }
    desired.current = value; userChoice.current = value; setSelected(value);
    errorSource.current = ''; setCaptionError(''); apply();
    const node = nodes().find(item => item.srclang === value);
    if (node?.readyState === HTMLTrackElement.ERROR) fail(`Не удалось загрузить субтитры: ${subtitleLabel(value)}. Повторите загрузку.`, 'track');
  };
  const validEvent = (node: HTMLTrackElement, generation: number) => alive.current && sessionIsCurrent() && generation === trackGeneration.current &&
    node.isConnected && Boolean(playerRef.current?.contains(node));
  const loaded = (node: HTMLTrackElement, generation: number) => {
    if (!validEvent(node, generation)) return;
    if (node.srclang === desired.current && errorSource.current === 'track') { errorSource.current = ''; setCaptionError(''); }
    apply();
  };
  const errored = (node: HTMLTrackElement, generation: number) => {
    if (validEvent(node, generation) && node.srclang === desired.current) fail(`Не удалось загрузить субтитры: ${subtitleLabel(node.srclang)}. Повторите загрузку.`, 'track');
  };
  const bindTrack = (node: HTMLTrackElement | null, generation: number) => {
    if (!node) return;
    // Track load/error do not bubble. React 18 does not install their native
    // listeners on <track>, so own them directly instead of relying on onLoad.
    node.onload = () => loaded(node, generation);
    node.onerror = () => errored(node, generation);
  };
  return { tracks, selected, captionGeneration, captionError, busy, needsVideoRestart: errorSource.current === 'revision', choose, bindTrack, apply, refresh };
}

/** Mount with key=video.id so a recording owns its original session and media node. */
const VideoPlaybackPane: React.FC<{ video: TrainingVideo; adminPreview?: boolean; subtitlesRefresh?: number }> = ({ video, adminPreview = false, subtitlesRefresh = 0 }) => {
  const playerRef = useRef<HTMLVideoElement | null>(null);
  const initialSeekDone = useRef(false);
  const metadataReady = useRef(false);
  const progressReady = useRef(adminPreview);
  const initialProgress = useRef(video.progress || null);
  const savedProgress = useRef<VideoProgress | null>(adminPreview ? null : initialProgress.current);
  const retrySeek = useRef<number | null>(null);
  const hasPlayed = useRef(false);
  const sessionToken = useRef(localStorage.getItem('token'));
  const previewMode = useRef(adminPreview);
  previewMode.current = adminPreview;
  const alive = useRef(true);
  const playbackErrorTimer = useRef<number | null>(null);
  const playbackController = useRef<AbortController | null>(null);
  const diagnosticController = useRef<AbortController | null>(null);
  const diagnosedGeneration = useRef<number | null>(null);
  const playbackRequest = useRef(0);
  const [progress, setProgress] = useState<VideoProgress | null>(savedProgress.current);
  const [playbackUrl, setPlaybackUrl] = useState('');
  const [subtitles, setSubtitles] = useState<VideoSubtitleTrack[]>([]);
  const [mediaGeneration, setMediaGeneration] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saveError, setSaveError] = useState('');

  const sessionIsCurrent = useCallback(() => localStorage.getItem('token') === sessionToken.current, []);
  const captions = useVideoSubtitles({ tracks: subtitles, playbackUrl, mediaGeneration, loading, playerRef,
    videoId: video.id, adminPreview, subtitlesRefresh, sessionIsCurrent });
  const stopErrorTimer = useCallback(() => {
    if (playbackErrorTimer.current !== null) window.clearTimeout(playbackErrorTimer.current);
    playbackErrorTimer.current = null;
  }, []);
  const clearPlaybackError = useCallback(() => {
    stopErrorTimer();
    if (sessionIsCurrent()) setError('');
  }, [sessionIsCurrent, stopErrorTimer]);
  const seekToSavedPosition = useCallback(() => {
    const player = playerRef.current;
    if (!player || !metadataReady.current || initialSeekDone.current || !progressReady.current) return;
    // A late response cannot move a recording the viewer has started or sought.
    if (hasPlayed.current) { initialSeekDone.current = true; return; }
    const position = savedProgress.current?.last_position_seconds || 0;
    if (position > 0 && Number.isFinite(player.duration) && position < player.duration - 3) player.currentTime = position;
    initialSeekDone.current = true;
  }, []);
  const loadPlayback = useCallback(async () => {
    if (!sessionIsCurrent()) { setError(SESSION_CHANGED); return; }
    const request = ++playbackRequest.current;
    playbackController.current?.abort();
    diagnosticController.current?.abort();
    const controller = new AbortController(); playbackController.current = controller;
    stopErrorTimer();
    setError(''); setLoading(true); setPlaying(false); setPlaybackUrl(''); setSubtitles([]);
    metadataReady.current = false;
    const prefix = adminPreview ? '/video/admin/videos' : '/video/videos';
    try {
      const { data } = await api.get<{ url: string; subtitles: VideoSubtitleTrack[]; expires_in_seconds?: number }>(`${prefix}/${video.id}/playback-url`, { signal: controller.signal });
      if (controller.signal.aborted || !alive.current || request !== playbackRequest.current || !sessionIsCurrent()) return;
      if (!data.url) throw new Error('Ссылка на видео не получена');
      setPlaybackUrl(resolvePlaybackUrl(data.url)); setSubtitles(data.subtitles || []);
      // A new keyed video starts loading its src, even when the URL is unchanged.
      // Calling load() again would interrupt that initial resource selection.
      setMediaGeneration(value => value + 1);
    } catch (err) {
      if (!controller.signal.aborted && alive.current && request === playbackRequest.current && sessionIsCurrent()) setError(getApiErrorMessage(err, 'Видео недоступно'));
    } finally {
      if (!controller.signal.aborted && alive.current && request === playbackRequest.current) {
        setLoading(false);
        if (!sessionIsCurrent()) setError(SESSION_CHANGED);
      }
    }
  }, [adminPreview, video.id, sessionIsCurrent, stopErrorTimer]);

  useEffect(() => {
    const controller = new AbortController(); alive.current = true;
    initialSeekDone.current = false; metadataReady.current = false;
    hasPlayed.current = false; retrySeek.current = null; progressReady.current = adminPreview;
    savedProgress.current = adminPreview ? null : initialProgress.current;
    setProgress(savedProgress.current); setSaveError('');
    void loadPlayback();
    // Progress cannot block obtaining a playback URL or displaying the player.
    if (!adminPreview && sessionIsCurrent()) {
      api.get<VideoProgress>(`/video/videos/${video.id}/progress`, { signal: controller.signal })
        .then(({ data }) => {
          if (controller.signal.aborted || !alive.current || !sessionIsCurrent()) return;
          savedProgress.current = data;
          setProgress(previous => ({ ...data,
            percent_watched: Math.max(previous?.percent_watched || 0, data.percent_watched),
            is_completed: Boolean(previous?.is_completed || data.is_completed),
          }));
        })
        .catch(() => {
          if (!controller.signal.aborted && alive.current && sessionIsCurrent()) setSaveError('Не удалось восстановить прогресс видео. Воспроизведение доступно; прогресс будет сохраняться во время просмотра.');
        })
        .finally(() => {
          if (controller.signal.aborted || !alive.current || !sessionIsCurrent()) return;
          progressReady.current = true; seekToSavedPosition();
        });
    }
    return () => { alive.current = false; controller.abort(); playbackController.current?.abort(); diagnosticController.current?.abort(); stopErrorTimer(); };
  }, [adminPreview, video.id, loadPlayback, sessionIsCurrent, seekToSavedPosition, stopErrorTimer]);

  const saveProgress = useCallback(async (complete = false) => {
    const player = playerRef.current;
    if (adminPreview || previewMode.current || !alive.current || !hasPlayed.current || !player || !Number.isFinite(player.duration) || player.duration <= 0) return;
    if (!sessionIsCurrent()) {
      setSaveError('Сессия изменилась. Откройте обучение заново, чтобы продолжить сохранение прогресса.'); return;
    }
    const payload = { position_seconds: player.currentTime, duration_seconds: player.duration };
    try {
      const { data } = complete
        ? await api.post<VideoProgress>(`/video/videos/${video.id}/complete`, payload)
        : await api.put<VideoProgress>(`/video/videos/${video.id}/progress`, payload);
      if (!alive.current || previewMode.current || !sessionIsCurrent()) return;
      setProgress(previous => ({ ...data,
        percent_watched: Math.max(previous?.percent_watched || 0, data.percent_watched),
        is_completed: Boolean(previous?.is_completed || data.is_completed),
      })); setSaveError('');
    } catch { if (alive.current && !previewMode.current && sessionIsCurrent()) setSaveError('Не удалось сохранить прогресс видео. Повторим при следующей паузе или во время просмотра.'); }
  }, [adminPreview, video.id, sessionIsCurrent]);

  useEffect(() => {
    if (adminPreview || !playing) return;
    const timer = window.setInterval(() => saveProgress(), 12000);
    return () => window.clearInterval(timer);
  }, [adminPreview, playing, saveProgress]);
  useEffect(() => {
    if (adminPreview) return;
    // Keep this recording's node: React clears the live ref before unmount cleanup.
    const player = playerRef.current;
    const flush = () => {
      const token = sessionToken.current;
      if (previewMode.current || !hasPlayed.current || !player || !token || !Number.isFinite(player.duration) || player.duration <= 0 || !sessionIsCurrent()) return;
      const base = String(api.defaults.baseURL || '/api/v1').replace(/\/$/, '');
      fetch(`${base}/video/videos/${video.id}/progress`, {
        method: 'PUT', keepalive: true,
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ position_seconds: player.currentTime, duration_seconds: player.duration }),
      }).catch(() => undefined);
    };
    window.addEventListener('pagehide', flush);
    return () => { window.removeEventListener('pagehide', flush); flush(); };
  }, [adminPreview, playbackUrl, mediaGeneration, video.id, sessionIsCurrent]);

  const loaded = () => {
    const player = playerRef.current;
    if (!player) return;
    clearPlaybackError(); metadataReady.current = true;
    const position = retrySeek.current;
    if (position !== null) {
      retrySeek.current = null;
      if (position > 0 && Number.isFinite(player.duration) && position < player.duration - 3) player.currentTime = position;
      initialSeekDone.current = true;
    } else seekToSavedPosition();
  };
  const playbackFailed = (event: React.SyntheticEvent<HTMLVideoElement>) => {
    if (event.target !== event.currentTarget) return;
    const player = event.currentTarget;
    const generation = mediaGeneration;
    stopErrorTimer();
    playbackErrorTimer.current = window.setTimeout(() => {
      playbackErrorTimer.current = null;
      if (!alive.current || playerRef.current !== player || !sessionIsCurrent() || !player.error) return;
      const code = nativeErrorCode(player.error.code);
      setError(mediaErrorText(code));
      if (diagnosedGeneration.current === generation) return;
      diagnosedGeneration.current = generation;
      diagnosticController.current?.abort();
      const controller = new AbortController(); diagnosticController.current = controller;
      const prefix = adminPreview ? '/video/admin/videos' : '/video/videos';
      // Refresh only the limited cookie and log bounded numeric facts. Never
      // apply returned media metadata or let a diagnostic401 redirect the page.
      void api.get(`${prefix}/${video.id}/playback-url`, {
        signal: controller.signal,
        params: {
          playback_error: code,
          ready_state: boundedMediaState(player.readyState, 4),
          network_state: boundedMediaState(player.networkState, 3),
          webm_support: codecSupport(player, 'video/webm; codecs="vp8, opus"'),
          mp4_support: codecSupport(player, 'video/mp4; codecs="avc1.42E01E, mp4a.40.2"'),
        },
        validateStatus: () => true,
        transformRequest: data => {
          // Axios request interceptors run before dispatch. A session changed
          // in that interval must not send this recording's diagnostic.
          if (!alive.current || playerRef.current !== player || !sessionIsCurrent() || controller.signal.aborted) {
            controller.abort(); throw new Error('Playback diagnostic cancelled');
          }
          return data;
        },
      }).catch(() => undefined);
    }, 500);
  };
  const retryPlayback = () => {
    if (loading) return;
    if (!sessionIsCurrent()) { setError(SESSION_CHANGED); return; }
    const player = playerRef.current;
    retrySeek.current = player && Number.isFinite(player.currentTime) && (hasPlayed.current || player.currentTime > 0) ? player.currentTime : null;
    initialSeekDone.current = hasPlayed.current; player?.pause(); void loadPlayback();
  };

  return <>
    {error && <Alert type="error" showIcon message={error} action={<Button icon={<ReloadOutlined />} loading={loading} onClick={retryPlayback}>Повторить запуск</Button>} />}
    {!adminPreview && saveError && <Alert type="warning" showIcon message={saveError} />}
    <div className="training-workspace-video-body">
      <div className="video-player-shell">
        {loading ? <Skeleton active /> : playbackUrl && <video key={mediaGeneration} ref={playerRef} src={playbackUrl} controls playsInline preload="metadata" aria-label={video.title}
          onLoadedMetadata={() => { loaded(); captions.apply(); }} onLoadedData={clearPlaybackError} onCanPlay={clearPlaybackError}
          onPlay={() => { hasPlayed.current = true; initialSeekDone.current = true; clearPlaybackError(); setPlaying(true); }}
          onPause={() => { setPlaying(false); void saveProgress(); }} onSeeked={() => { initialSeekDone.current = true; void saveProgress(); }}
          onEnded={() => { setPlaying(false); void saveProgress(true); }} onError={playbackFailed}>
          {captions.tracks.map(track => <track key={`${track.language}:${captions.captionGeneration}`} data-subtitle-language={track.language}
            kind="subtitles" src={resolvePlaybackUrl(track.url)} srcLang={track.language} label={subtitleLabel(track.language)}
            ref={node => captions.bindTrack(node, captions.captionGeneration)} />)}
        </video>}
      </div>
      <div className="training-workspace-video-info">
        <Space wrap><Typography.Text strong>{video.title}</Typography.Text>
          {!adminPreview && progress?.is_completed && <Tag icon={<CheckCircleFilled />} color="success">Просмотрено</Tag>}</Space>
        {video.description && <Typography.Paragraph type="secondary">{video.description}</Typography.Paragraph>}
        {!loading && playbackUrl && <div className="video-subtitles">
          {captions.tracks.length > 0 ? <label className="video-subtitles-control">Субтитры
            <select aria-label="Субтитры" value={captions.selected} disabled={captions.busy} onChange={event => captions.choose(event.target.value as SubtitleChoice)}>
              <option value="off">Выкл</option>{captions.tracks.map(track => <option key={track.language} value={track.language}>{subtitleLabel(track.language)}</option>)}
            </select></label> : !captions.captionError && <Typography.Text type="secondary">Субтитры для этой записи пока не созданы</Typography.Text>}
          {captions.busy && <Typography.Text type="secondary">Обновляем субтитры…</Typography.Text>}
          {captions.captionError && <Alert type="warning" showIcon message={captions.captionError}
            action={captions.needsVideoRestart
              ? <Button onClick={retryPlayback}>Повторить запуск</Button>
              : <Button loading={captions.busy} onClick={() => void captions.refresh()}>Повторить загрузку субтитров</Button>} />}
        </div>}
        {adminPreview ? <Typography.Text type="secondary">Предпросмотр без изменения статистики</Typography.Text> : <>
          <Progress percent={Math.round(progress?.percent_watched || 0)} />
          <Typography.Text type="secondary">Прогресс сохраняется во время просмотра. Видео засчитывается после 90%.</Typography.Text>
        </>}
      </div>
    </div>
  </>;
};

export default VideoPlaybackPane;
